export class IntersectionWatcher {
  constructor(observerOptions = {}) {
    this.observerOptions = observerOptions;
    this.observer = new IntersectionObserver(
      this._handleIntersect.bind(this),
      this.observerOptions,
    );
    this.elements = new WeakMap(); // WeakMap<HTMLElement, { resolve, promise, resolved }>
  }

  _handleIntersect(entries) {
    for (const entry of entries) {
      const element = entry.target;
      const record = this.elements.get(element);

      if (!record) continue;

      if (entry.isIntersecting && !record.resolved) {
        record.resolved = true;
        record.resolve(entry);
      } else if (!entry.isIntersecting && record.resolved) {
        // Reset with a new promise when it leaves viewport
        this._createNewPromiseFor(element);
      }
    }
  }

  /**
   * Returns a Promise that resolves when the element enters the viewport.
   * @param {HTMLElement} element
   * @returns {Promise<IntersectionObserverEntry>}
   */
  intersecting(element) {
    if (!this.elements.has(element)) {
      this._createNewPromiseFor(element);
      this.observer.observe(element);
    }

    return this.elements.get(element).promise;
  }

  _createNewPromiseFor(element) {
    let resolveFn;
    const promise = new Promise((resolve) => {
      resolveFn = resolve;
    });

    this.elements.set(element, {
      promise,
      resolve: resolveFn,
      resolved: false,
    });
  }

  /**
   * Manually unobserve an element.
   * @param {HTMLElement} element
   */
  unobserve(element) {
    if (this.elements.has(element)) {
      this.observer.unobserve(element);
      this.elements.delete(element);
    }
  }

  /**
   * Disconnect the observer and clear all tracking.
   */
  disconnect() {
    this.observer.disconnect();
    this.elements.clear();
  }
}
